import { Button } from '@/ui';

import AppointmentIssuesAlert from './AppointmentIssuesAlert';
import AppointmentStatusAlert from './AppointmentStatusAlert';

import type { BrowserAppointmentView } from './types';

interface AppointmentCardProps {
  appointment: BrowserAppointmentView;
  onCancel?: () => void;
  onReschedule?: () => void;
}

export const formatAppointmentDate = (
  value: string,
  timeZone: string,
): string =>
  new Date(value).toLocaleDateString('en', {
    day: 'numeric',
    month: 'long',
    timeZone,
    weekday: 'long',
  });

export const formatAppointmentTimeRange = (
  start: string,
  end: string,
  timeZone: string,
): string => {
  const options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    hour12: true,
    minute: '2-digit',
    timeZone,
  };
  const startTime = new Date(start).toLocaleTimeString('en', options);
  const endTime = new Date(end).toLocaleTimeString('en', options);
  const startMeridiem = startTime.slice(-2);
  const endMeridiem = endTime.slice(-2);
  const timeZoneName = new Intl.DateTimeFormat('en', {
    timeZone,
    timeZoneName: 'short',
  })
    .formatToParts(new Date(start))
    .find((part) => part.type === 'timeZoneName')?.value;
  const range = `${
    startMeridiem === endMeridiem
      ? startTime.replace(/\s?[AP]M$/u, '')
      : startTime
  }-${endTime}`;
  return timeZoneName ? `${range} ${timeZoneName}` : range;
};

export const formatAppointmentStatus = (
  status: BrowserAppointmentView['lifecycleStatus'],
): string =>
  status
    .split('_')
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ');

export const isHttpsAppointmentUrl = (value: string | null): boolean => {
  if (!value) return false;
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
};

export const hasRenderableAppointmentIdentity = (
  appointment: BrowserAppointmentView,
) =>
  Boolean(
    appointment.id &&
    appointment.expectedIdentities.coach.personId &&
    appointment.expectedIdentities.member.personId,
  );

export const AppointmentCard = ({
  appointment,
  onCancel = undefined,
  onReschedule = undefined,
}: AppointmentCardProps) => {
  if (!hasRenderableAppointmentIdentity(appointment)) {
    return <div>Appointment unavailable</div>;
  }

  const isCancelled =
    appointment.lifecycleStatus === 'cancelled' ||
    appointment.lifecycleStatus === 'cancellation_pending';
  const isDone = Date.parse(appointment.currentSchedule.endAt) < Date.now();
  const showJoin =
    appointment.actionCapabilities.join.allowed &&
    isHttpsAppointmentUrl(appointment.meeting.joinUrl) &&
    !isCancelled &&
    !isDone;
  const showReschedule =
    Boolean(onReschedule) &&
    appointment.actionCapabilities.reschedule.allowed &&
    appointment.actionCapabilities.reschedule.scopes.includes('appointment') &&
    !isCancelled &&
    !isDone;
  const showCancel =
    Boolean(onCancel) &&
    appointment.actionCapabilities.cancel.allowed &&
    appointment.actionCapabilities.cancel.scopes.includes('appointment') &&
    !isCancelled &&
    !isDone;
  const timeZone = appointment.currentSchedule.timezone;

  return (
    <article>
      <h3>{appointment.expectedIdentities.coach.displayName}</h3>
      <AppointmentStatusAlert appointment={appointment} />
      <div>{formatAppointmentStatus(appointment.lifecycleStatus)}</div>
      <div>
        {formatAppointmentDate(appointment.currentSchedule.startAt, timeZone)}
      </div>
      <div>
        {formatAppointmentTimeRange(
          appointment.currentSchedule.startAt,
          appointment.currentSchedule.endAt,
          timeZone,
        )}
      </div>
      <AppointmentIssuesAlert appointment={appointment} />
      {showJoin && appointment.meeting.joinUrl && (
        <a href={appointment.meeting.joinUrl} rel="noreferrer" target="_blank">
          Join session
        </a>
      )}
      {showReschedule && (
        <Button variant="outlined" onClick={onReschedule}>
          Reschedule
        </Button>
      )}
      {showCancel && (
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
      )}
    </article>
  );
};

export default AppointmentCard;
